import { Invite, InvitesApi, Member, MembersApi, SuccessResponse } from '@citruscamps/citrus-client'
import { generateApiConfig } from '../../../../../utils/client-config'
import { generateBaseKey } from '../../../../../utils/key-generator'
import { useQueryClient } from 'react-query'
import { useRequestHandler } from '../../../../../hooks/useRequestHandler'
import { useToast } from '../../../../../hooks/useToast'
import translations from '../../../../../translations/en.json'

interface IProps {
  programId?: string
}

interface IHandleMembers {
  handleActivate: (values: Member) => Promise<void>
  handleDeactivate: (values: Member) => Promise<void>
  handleResendInvite: (values: Member) => Promise<void>
  handleRevokeInvite: (values: Member) => Promise<void>
}

export const useHandleMembers = ({ programId }: IProps): IHandleMembers => {
  const { requestHandler } = useRequestHandler()
  const queryClient = useQueryClient()
  const { setToast } = useToast()
  const handleActivate = async (values: Member) => {
    const memberClient = new MembersApi(generateApiConfig())
    try {
      await requestHandler<Member>(
        () => memberClient.activateMember({ program_id: values.program.id, member_id: values.id }),
        {
          toast: {
            setToast,
          },
        },
      )
      await queryClient.invalidateQueries(
        generateBaseKey({
          type: 'member',
          program_id: programId,
        }),
      )
    } catch {
      setToast({
        header: (translations as any)['error.default_toast_header'],
        message: (translations as any)['error.default_toast_message'],
        type: 'danger',
      })
    }
  }
  const handleDeactivate = async (values: Member) => {
    const memberClient = new MembersApi(generateApiConfig())
    try {
      await requestHandler<Member>(
        () =>
          memberClient.deactivateMember({ program_id: values.program.id, member_id: values.id }),
        {
          toast: {
            setToast,
          },
        },
      )
      await queryClient.invalidateQueries(
        generateBaseKey({
          type: 'member',
          program_id: programId,
        }),
      )
    } catch {
      setToast({
        header: (translations as any)['error.default_toast_header'],
        message: (translations as any)['error.default_toast_message'],
        type: 'danger',
      })
    }
  }

  const handleResendInvite = async (values: Member) => {
    const inviteClient = new InvitesApi(generateApiConfig())
    try {
      await requestHandler<Invite>(
        () =>
          inviteClient.resendProgramInvite({
            program_id: values.program.id,
            invite_id: values?.invite_id || '',
          }),
        {
          toast: {
            setToast,
          },
        },
      )
    } catch {
      setToast({
        header: (translations as any)['error.default_toast_header'],
        message: (translations as any)['error.default_toast_message'],
        type: 'danger',
      })
    }
  }

  const handleRevokeInvite = async (values: Member): Promise<void> => {
    const inviteClient = new InvitesApi(generateApiConfig())
    try {
      await requestHandler<SuccessResponse>(
        () =>
          inviteClient.deleteProgramInvite({
            program_id: values.program.id,
            invite_id: values?.invite_id || '',
          }),
        {
          toast: {
            setToast,
          },
        },
      )
      await queryClient.invalidateQueries(
        generateBaseKey({
          type: 'member',
          program_id: programId,
        }),
      )
    } catch {
      setToast({
        header: (translations as any)['error.default_toast_header'],
        message: (translations as any)['error.default_toast_message'],
        type: 'danger',
      })
    }
  }
  return {
    handleActivate,
    handleDeactivate,
    handleResendInvite,
    handleRevokeInvite,
  }
}
